import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, Input, Button, Space, Card, Tag, Row, Col } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { GET_GROUPS } from '../../mutations/groups';

const { Option } = Select;

// Opções de campos disponíveis para filtro
const fieldOptions = [
  { label: 'Código', value: 'reference', type: 'text' },
  { label: 'Nome', value: 'name', type: 'text' },
  { label: 'Marca', value: 'brand', type: 'text' },
  { label: 'Descrição', value: 'description', type: 'text' },
  { label: 'Grupo', value: 'group', type: 'array' },
  { label: 'Imagem', value: 'hasImage', type: 'boolean' },
  { label: 'Tabela de Preço', value: 'hasPriceSegmentation', type: 'boolean' },
  { label: 'Informações Adicionais', value: 'hasInfos', type: 'boolean' },
];

// Operadores por tipo de campo
const operatorsByType = {
  text: [
    { label: 'Contém', value: 'contains' },
    { label: 'Não contém', value: 'not_contains' },
    { label: 'É exatamente', value: 'equals' },
    { label: 'Está em branco', value: 'is_empty' },
    { label: 'Está preenchido', value: 'is_not_empty' },
  ],
  array: [
    { label: 'Contém', value: 'contains' },
    { label: 'Não contém', value: 'not_contains' },
    { label: 'É exatamente', value: 'equals' },
    { label: 'Está vazio', value: 'is_empty' },
    { label: 'Não está vazio', value: 'is_not_empty' },
  ],
  boolean: [
    { label: 'Tem', value: 'has' },
    { label: 'Não tem', value: 'has_not' },
  ]
};

const AdvancedProductFilterModal = ({ open, onCancel, onApply, initialFilters = [] }) => {
  const [form] = Form.useForm();
  const [filters, setFilters] = useState([]);
  const [formValues, setFormValues] = useState({});

  // Buscar grupos para o filtro de grupos
  const { data: groupsData } = useQuery(GET_GROUPS, {
    variables: { page: 1, perPage: 1000, search: '' }, // Buscar todos os grupos
    skip: !open
  });

  const groups = groupsData?.groups?.edges?.map(edge => edge.node) || [];

  useEffect(() => {
    if (open) {
      if (initialFilters.length > 0) {
        // Converter filtros iniciais
        const convertedFilters = initialFilters.map((filter, index) => ({
          id: filter.id || Date.now() + index,
          field: filter.field,
          operator: filter.operator,
          fieldType: filter.fieldType
        }));
        
        setFilters(convertedFilters);
        
        // Preencher formulário
        const initialValues = {};
        initialFilters.forEach((filter, index) => {
          initialValues[`field_${index}`] = filter.field;
          initialValues[`operator_${index}`] = filter.operator;
          initialValues[`value_${index}`] = filter.value;
        });
        
        form.setFieldsValue(initialValues);
        setFormValues(initialValues);
      } else {
        addFilter();
      }
    } else {
      setFilters([]);
      form.resetFields();
      setFormValues({});
    }
  }, [open, initialFilters]);

  const addFilter = () => {
    setFilters(prevFilters => {
      const newFilter = {
        id: Date.now(),
        field: 'name',
        operator: 'contains',
        fieldType: 'text'
      };
      const newFilters = [...prevFilters, newFilter];

      // Inicializar valores padrão no form
      const newIndex = prevFilters.length;
      const defaultValues = {
        [`field_${newIndex}`]: 'name',
        [`operator_${newIndex}`]: 'contains'
      };
      form.setFieldsValue(defaultValues);
      setFormValues(prev => ({ ...prev, ...defaultValues }));

      return newFilters;
    });
  };

  const removeFilter = (filterId) => {
    const filterIndex = filters.findIndex(f => f.id === filterId);
    const newFilters = filters.filter(f => f.id !== filterId);
    setFilters(newFilters);

    // Reorganizar campos do form após remoção
    const currentValues = form.getFieldsValue();
    const newValues = {};

    newFilters.forEach((filter, newIndex) => {
      const oldIndex = filters.findIndex(f => f.id === filter.id);
      if (oldIndex !== -1 && oldIndex !== newIndex) {
        // Mover valores para o novo índice
        Object.keys(currentValues).forEach(key => {
          if (key.includes(`_${oldIndex}`)) {
            const newKey = key.replace(`_${oldIndex}`, `_${newIndex}`);
            newValues[newKey] = currentValues[key];
          }
        });
      } else if (oldIndex === newIndex) {
        // Manter valores no mesmo índice
        Object.keys(currentValues).forEach(key => {
          if (key.includes(`_${oldIndex}`)) {
            newValues[key] = currentValues[key];
          }
        });
      }
    });

    form.setFieldsValue(newValues);
    setFormValues(newValues);
  };

  const handleApply = () => {
    form.validateFields().then(values => {
      const processedFilters = filters.map((filter, index) => {
        const field = values[`field_${index}`];
        const operator = values[`operator_${index}`];
        const value = values[`value_${index}`];
        const fieldConfig = fieldOptions.find(opt => opt.value === field);

        return {
          id: filter.id,
          field,
          operator,
          value,
          fieldType: fieldConfig?.type || 'text'
        };
      }).filter(filter => {
        // Remover filtros incompletos
        if (['is_empty', 'is_not_empty', 'has', 'has_not'].includes(filter.operator)) {
          return true; // Estes operadores não precisam de valor
        }
        return filter.value !== undefined && filter.value !== null && filter.value !== '';
      });

      onApply(processedFilters);
    }).catch(errorInfo => {
      console.error('Validation Failed:', errorInfo);
    });
  };

  const renderValueInput = (filter, index) => {
    const field = formValues[`field_${index}`] || 'name';
    const operator = formValues[`operator_${index}`] || 'contains';
    const fieldConfig = fieldOptions.find(opt => opt.value === field);

    // Operadores que não precisam de valor
    if (['is_empty', 'is_not_empty', 'has', 'has_not'].includes(operator)) {
      return null;
    }

    if (field === 'group') {
      // Para "contains", permitir múltipla seleção
      const isMultipleAllowed = operator === 'contains';
      
      return (
        <Form.Item
          name={`value_${index}`}
          label={isMultipleAllowed ? "Grupos" : "Grupo"}
          rules={[{ required: true, message: isMultipleAllowed ? 'Selecione pelo menos um grupo' : 'Selecione o grupo' }]}
        >
          <Select
            placeholder={isMultipleAllowed ? "Selecione os grupos" : "Selecione o grupo"}
            allowClear
            showSearch
            mode={isMultipleAllowed ? "multiple" : undefined}
            maxTagCount="responsive"
            filterOption={(input, option) => {
              // Buscar o nome do grupo pelo value (id)
              const group = groups.find(g => g.id === option.value);
              return group?.name.toLowerCase().indexOf(input.toLowerCase()) >= 0;
            }}
          >
            {groups.map(group => (
              <Option key={group.id} value={group.id}>
                {group.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      );
    }

    return (
      <Form.Item
        name={`value_${index}`}
        label="Valor"
        rules={[{ required: true, message: 'Digite o valor' }]}
      >
        <Input placeholder="Digite o valor para filtrar" />
      </Form.Item>
    );
  };

  const onFormValuesChange = (changedValues, allValues) => {
    setFormValues(allValues);
    
    // Se mudou o campo, resetar o operador para o padrão do tipo
    Object.keys(changedValues).forEach(key => {
      if (key.startsWith('field_')) {
        const index = key.split('_')[1];
        const field = changedValues[key];
        const fieldConfig = fieldOptions.find(opt => opt.value === field);
        const defaultOperator = operatorsByType[fieldConfig?.type || 'text'][0].value;
        
        const newValues = {
          [`operator_${index}`]: defaultOperator
        };
        
        // Limpar valor apenas se não for campo boolean
        if (fieldConfig?.type !== 'boolean') {
          newValues[`value_${index}`] = undefined;
        }
        
        form.setFieldsValue(newValues);
        
        // Atualizar o filtro na lista
        setFilters(prevFilters => {
          const newFilters = [...prevFilters];
          if (newFilters[index]) {
            newFilters[index].field = field;
            newFilters[index].operator = defaultOperator;
            newFilters[index].fieldType = fieldConfig?.type || 'text';
          }
          return newFilters;
        });
      }
      
      // Se mudou o operador para um campo group, limpar o valor
      if (key.startsWith('operator_')) {
        const index = key.split('_')[1];
        const operator = changedValues[key];
        const field = allValues[`field_${index}`];
        
        if (field === 'group') {
          // Limpar valor quando trocar de operador para evitar conflitos entre single/multiple
          const newValues = {
            [`value_${index}`]: undefined
          };
          form.setFieldsValue(newValues);
        }
      }
    });
  };

  const getFilterDescription = (filter, index) => {
    const field = formValues[`field_${index}`] || filter.field || 'name';
    const operator = formValues[`operator_${index}`] || filter.operator || 'contains';
    
    const fieldLabel = fieldOptions.find(opt => opt.value === field)?.label || field;
    const fieldConfig = fieldOptions.find(opt => opt.value === field);
    const operatorLabel = operatorsByType[fieldConfig?.type || 'text'].find(opt => opt.value === operator)?.label || operator;

    return `${fieldLabel} - ${operatorLabel}`;
  };

  return (
    <Modal
      title="Filtros Avançados de Produtos"
      open={open}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancelar
        </Button>,
        <Button key="clear" onClick={() => {
          setFilters([]);
          form.resetFields();
          onApply([]);
        }}>
          Limpar Filtros
        </Button>,
        <Button key="apply" type="primary" onClick={handleApply}>
          Aplicar Filtros
        </Button>
      ]}
      width={900}
      maskClosable={false}
    >
      <Form form={form} layout="vertical" onValuesChange={onFormValuesChange}>
        {filters.map((filter, index) => (
          <Card
            key={filter.id}
            size="small"
            style={{ marginBottom: 16 }}
            title={
              <Space>
                <span>Filtro {index + 1}</span>
                <Tag color="blue">{getFilterDescription(filter, index)}</Tag>
              </Space>
            }
            extra={
              filters.length > 1 && (
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeFilter(filter.id)}
                >
                  Remover
                </Button>
              )
            }
          >
            <Row gutter={16}>
              {(() => {
                const field = formValues[`field_${index}`] || 'name';
                const fieldConfig = fieldOptions.find(opt => opt.value === field);
                const isBoolean = fieldConfig?.type === 'boolean';
                
                return (
                  <>
                    <Col span={isBoolean ? 12 : 8}>
                      <Form.Item
                        name={`field_${index}`}
                        label="Campo"
                        initialValue="name"
                        rules={[{ required: true, message: 'Selecione o campo' }]}
                      >
                        <Select placeholder="Selecione o campo">
                          {fieldOptions.map(option => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={isBoolean ? 12 : 8}>
                      <Form.Item
                        name={`operator_${index}`}
                        label="Operador"
                        initialValue="contains"
                        rules={[{ required: true, message: 'Selecione o operador' }]}
                      >
                        <Select placeholder="Selecione o operador">
                          {(() => {
                            const operators = operatorsByType[fieldConfig?.type || 'text'] || operatorsByType.text;
                            return operators.map(option => (
                              <Option key={option.value} value={option.value}>
                                {option.label}
                              </Option>
                            ));
                          })()}
                        </Select>
                      </Form.Item>
                    </Col>
                    {!isBoolean && (
                      <Col span={8}>
                        {renderValueInput(filter, index)}
                      </Col>
                    )}
                  </>
                );
              })()}
            </Row>
          </Card>
        ))}

        <Button
          type="dashed"
          onClick={addFilter}
          icon={<PlusOutlined />}
          style={{ width: '100%', marginTop: 16 }}
        >
          Adicionar Filtro
        </Button>
      </Form>
    </Modal>
  );
};

export default AdvancedProductFilterModal; 